import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { CheckCircle, AlertCircle } from "lucide-react";
import { HugeiconsIcon } from "@hugeicons/react";
import SentIcon from "@hugeicons/core-free-icons/SentIcon";
import { submitContactForm } from "@/lib/contact.server";
import { useTranslation } from "@/lib/i18n";

export default function ContactForm({
  embedded = false,
}: {
  embedded?: boolean;
}) {
  const { translations: t } = useTranslation();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");
    setErrorMessage("");

    try {
      await submitContactForm({ data: formData });
      setSubmitStatus("success");
      setFormData({ fullName: "", email: "", subject: "", message: "" });
    } catch (error) {
      setSubmitStatus("error");
      setErrorMessage(
        error instanceof Error
          ? error.message
          : t.contactForm.errorMessage
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={embedded ? "w-full h-full" : "w-full"}>
      <div className="flex items-center gap-3 mb-6 px-4 lg:px-6 pt-6">
        <div className="w-8 h-8 rounded-lg bg-green-50 dark:bg-green-900/20 flex items-center justify-center transition-colors">
          <HugeiconsIcon icon={SentIcon} size={16} color="currentColor" className="text-green-600 dark:text-green-400" />
        </div>
        <div>
          <h4 className="font-bold text-gray-900 dark:text-white text-base">
            {t.contactForm.heading}
          </h4>
          <p className="text-gray-600 dark:text-gray-400 text-xs">
            {t.contactForm.description}
          </p>
        </div>
      </div>

      <div className="relative h-full">
        {!embedded && (
          <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-4/5 h-8 bg-black/10 dark:bg-white/5 rounded-full blur-md z-0"></div>
        )}

        <div
          className={`
          ${
            embedded
              ? "bg-transparent"
              : "border border-solid border-gray-300/30 dark:border-neutral-700 rounded-lg overflow-hidden bg-white dark:bg-neutral-900 shadow-sm"
          } relative z-10 transition-colors h-full`}
        >
          <form onSubmit={handleSubmit} className="px-4 lg:px-6 pb-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="fullName"
                  className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-2"
                >
                  {t.contactForm.fullName}
                </label>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  placeholder={t.contactForm.fullNamePlaceholder}
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="h-12 bg-gray-50/50 dark:bg-neutral-800 border-gray-200 dark:border-neutral-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-white dark:focus:bg-neutral-800 transition-all duration-300 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-500 rounded-lg"
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-2"
                >
                  {t.contactForm.emailAddress}
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t.contactForm.emailPlaceholder}
                  value={formData.email}
                  onChange={handleInputChange}
                  className="h-12 bg-gray-50/50 dark:bg-neutral-800 border-gray-200 dark:border-neutral-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-white dark:focus:bg-neutral-800 transition-all duration-300 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-500 rounded-lg"
                  required
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="subject"
                className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-2"
              >
                {t.contactForm.subject}
              </label>
              <Input
                id="subject"
                name="subject"
                type="text"
                placeholder={t.contactForm.subjectPlaceholder}
                value={formData.subject}
                onChange={handleInputChange}
                className="h-12 bg-gray-50/50 dark:bg-neutral-800 border-gray-200 dark:border-neutral-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-white dark:focus:bg-neutral-800 transition-all duration-300 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-500 rounded-lg"
                required
              />
            </div>

            <div>
              <label
                htmlFor="message"
                className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-2"
              >
                {t.contactForm.message}
              </label>
              <Textarea
                id="message"
                name="message"
                rows={embedded ? 8 : 6}
                placeholder={t.contactForm.messagePlaceholder}
                value={formData.message}
                onChange={handleInputChange}
                className="bg-gray-50/50 dark:bg-neutral-800 border-gray-200 dark:border-neutral-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-white dark:focus:bg-neutral-800 transition-all duration-300 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-500 resize-none rounded-lg"
                required
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-2">
              <div className="hover:scale-[1.02] active:scale-[0.98] transition-transform">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-3 bg-primary-gradient hover:brightness-[0.97] text-primary-foreground font-bold rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed h-12"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>{t.contactForm.sending}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <HugeiconsIcon icon={SentIcon} size={16} color="currentColor" />
                      <span>{t.contactForm.sendMessage}</span>
                    </div>
                  )}
                </Button>
              </div>
            </div>

            {/* Status Messages */}
            {submitStatus === "success" && (
              <div className="flex items-center gap-3 text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 rounded-lg mt-6 animate-in slide-in-from-bottom-2">
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">
                  {t.contactForm.successMessage}
                </span>
              </div>
            )}

            {submitStatus === "error" && (
              <div className="flex items-center gap-3 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-lg mt-6 animate-in slide-in-from-bottom-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">
                  {errorMessage || t.contactForm.errorMessage}
                </span>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
